/**
 * The first /authorize call of a session cannot know the dealer account: resolving it needs
 * the accounts query, which needs a token. Persisting the pairs we have already resolved lets
 * the next sign-in send account_id up front and skip the follow-up re-authorize redirect.
 */
import { ROUTE_PARAMS } from "@/constants";
import { shouldSkipAccountIdAuthorize } from "@/provider/AuthProvider/accountIdAuthPolicy";
import { isCdhIdIamIdMigrationEnabled } from "@/store/dxpIamAuthHeader";
import { matchPath } from "react-router";
import * as z from "zod";

export const REMEMBERED_ACCOUNTS_STORAGE_KEY = "dxp-remembered-accounts";

const accountPairSchema = z.object({
  cdhId: z.string(),
  iamId: z.string(),
});

const rememberedAccountsSchema = z.object({
  primary: accountPairSchema.optional(),
  last: accountPairSchema.optional(),
});

export type AccountPair = z.infer<typeof accountPairSchema>;
export type RememberedAccounts = z.infer<typeof rememberedAccountsSchema>;

export const readRememberedAccounts = (): RememberedAccounts | null => {
  try {
    const raw = localStorage.getItem(REMEMBERED_ACCOUNTS_STORAGE_KEY);
    if (raw === null) {
      return null;
    }

    return rememberedAccountsSchema.parse(JSON.parse(raw));
  } catch {
    // corrupt JSON or blocked storage — sign-in just misses the optimisation
    return null;
  }
};

export const writeRememberedAccounts = (
  accounts: RememberedAccounts | null
): void => {
  try {
    if (accounts === null) {
      localStorage.removeItem(REMEMBERED_ACCOUNTS_STORAGE_KEY);
      return;
    }

    localStorage.setItem(
      REMEMBERED_ACCOUNTS_STORAGE_KEY,
      JSON.stringify(accounts)
    );
  } catch {
    // private mode / blocked storage — in-memory only
  }
};

export const rememberAccounts = (accounts: {
  primary?: AccountPair;
  last: AccountPair;
}): void => {
  const current = readRememberedAccounts();

  writeRememberedAccounts({
    primary: accounts.primary ?? current?.primary,
    last: accounts.last,
  });
};

export const forgetRememberedAccounts = (): void => {
  writeRememberedAccounts(null);
};

/**
 * Resolves the IAM account id to authorize for, given the URL the user landed on.
 * Returns undefined rather than a best guess when the path names an unknown account —
 * a wrong account_id costs the same extra redirect as sending none, with more risk.
 */
export const getRememberedIamAccountId = (
  pathname: string
): string | undefined => {
  if (shouldSkipAccountIdAuthorize()) {
    return undefined;
  }

  const remembered = readRememberedAccounts();
  if (remembered === null) {
    return undefined;
  }

  const match = matchPath(`/:${ROUTE_PARAMS.ACCOUNT_ID}/*`, pathname);
  const routeIamId = match?.params[ROUTE_PARAMS.ACCOUNT_ID];

  if (routeIamId === undefined || routeIamId.length === 0) {
    return remembered.primary?.iamId;
  }

  if (isCdhIdIamIdMigrationEnabled()) {
    return [remembered.primary, remembered.last].find(
      (pair) => pair?.iamId === routeIamId
    )?.iamId;
  }

  return [remembered.primary, remembered.last].find(
    (pair) => pair?.cdhId === routeIamId
  )?.iamId;
};
